/**
 * License attestation (BYOL / "Stand der Technik") — made real from the mock.
 *
 * For a subscription that uses licensed norms, the operator attests — auditably — that the company
 * holds a valid license COVERING USE WITHIN THIS PLATFORM (scope-accurate, not a bare "a license
 * exists"), or orders the norm via the provider. Only norms in (conformity class ∩ captured asset
 * base) are shown, grouped by rights holder. Toggling writes an append-only record (who/when);
 * attestations are shared per provider for license verification (disclosed here). Copy is "Du".
 */
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { ExternalLink, Info, Loader2 } from "lucide-react";

import { useModalContext } from "../../components/ModalProvider";
import { authenticationAtom } from "../../State";
import { useLegalBindingAPI } from "../../LegalBindingAPI";
import { regulatedObjectName } from "../../administration/operator-responsibility/legalDisplay";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

// Provider logo assets (used with permission), keyed by rights-holder id at /logos/<provider>.png.
// Any provider without a file here falls back to the initials badge, so the row never breaks.
const LOGO_SRC: Record<string, string> = {
  vdi: "/logos/vdi.png",
  vdma: "/logos/vdma.png",
  din: "/logos/din.png",
  dvgw: "/logos/dvgw.png",
  gefma: "/logos/gefma.png",
};

// Radix reserves the empty string as a Select value — "alle Anbieter" needs a sentinel.
const ALL_PROVIDERS = "__all__";

// The real logo where we have it; a graceful initials badge otherwise (and on load error).
function ProviderLogo({ provider, name }: { provider: string; name: string }) {
  const [failed, setFailed] = useState(false);
  const src = LOGO_SRC[provider];
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name}
        title={name}
        // Maße + lazy: die Zeile springt beim Nachladen nicht, und weit unten
        // liegende Anbieter-Logos werden erst bei Bedarf geholt.
        width={80}
        height={24}
        loading="lazy"
        onError={() => setFailed(true)}
        className="h-6 max-w-20 shrink-0 object-contain"
      />
    );
  }
  return (
    <div
      className="flex h-6 w-10 shrink-0 items-center justify-center rounded-sm bg-muted text-[10px] font-bold text-muted-foreground"
      title={name}
    >
      {name.slice(0, 4).toUpperCase()}
    </div>
  );
}

export default function LicenseAttestationPanel({ canWrite = true }: { canWrite?: boolean }) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const [authentication] = useAtom(authenticationAtom) as any;
  const api = useLegalBindingAPI(authentication?.customer);
  const fieldId = useId();

  const [bindings, setBindings] = useState<any[]>([]);
  /*
   * Die Liste braucht Sekunden — sie zählt je Abonnement die Anlagen (16.10.2026: 31.808 Anlagen, 5 s).
   * Bis dahin war die Auswahl leer und sah aus wie „kein Abonnement“; ohne Abonnement sagte die Seite gar
   * nichts. Beides ist jetzt ein eigener Zustand, und der Eintrag nennt den Standort und den Status.
   */
  const [bindingsLoaded, setBindingsLoaded] = useState(false);
  const [sites, setSites] = useState<any[]>([]);
  const [subscriptionId, setSubscriptionId] = useState("");
  const [data, setData] = useState<any>(null);
  const [filterProvider, setFilterProvider] = useState("");
  const [filterText, setFilterText] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  /** „Werk Feuerbach — Best Practice · aktiviert“: the site (or Portfolio), the class, the state. */
  const subscriptionLabel = (b: any): string => {
    const anchor =
      b?.scope?.anchor?.type === "Site"
        ? sites.find((site: any) => String(site._id) === String(b.scope.anchor.siteId))?.name ||
          t("operatorObligationsPanel.site")
        : t("operatorObligationsPanel.portfolio");
    const conformance = t(`operatorObligationsPanel.conformance.${b.conformanceClass}`, b.conformanceClass);
    const state =
      b.status === "ended"
        ? t("licenseAttestation.ended", "beendet")
        : b.activation === "activated"
          ? t("operatorObligationsPanel.activation.activated")
          : b.activation === "stopped"
            ? t("operatorObligationsPanel.activation.stopped")
            : t("operatorObligationsPanel.activation.none");
    return `${anchor} — ${conformance} · ${state}`;
  };

  const subscriptionSelectId = `${fieldId}-subscription`;
  const providerSelectId = `${fieldId}-provider`;
  const searchId = `${fieldId}-search`;

  useEffect(() => {
    setSubscriptionId("");
    setBindingsLoaded(false);
    setData(null);
    setFilterProvider("");
    setFilterText("");
    let cancelled = false;
    (async () => {
      try {
        // Running subscriptions first — an ended one has no licenses to attest, and it was the default
        // whenever it happened to be the newest (15.10.2026: „keine Lizenzen“).
        const [result, siteList] = await Promise.all([api.getBindings(), api.getSites().catch(() => [])]);
        const bs = [...(result || [])].sort(
          (a: any, b: any) => Number(a.status === "ended") - Number(b.status === "ended")
        );
        if (!cancelled) {
          setBindings(bs);
          setSites(siteList || []);
          if (bs.length) setSubscriptionId(String(bs[0]._id));
        }
      } catch (error) {
        if (!cancelled) {
          pushError(error);
        }
      } finally {
        if (!cancelled) setBindingsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer]);

  const load = async () => {
    if (!subscriptionId) return;
    try {
      setData(await api.getLicenseAttestations(subscriptionId));
    } catch (error) {
      pushError(error);
    }
  };
  useEffect(() => {
    if (!subscriptionId) return;
    setData(null);
    let cancelled = false;
    (async () => {
      try {
        const result = await api.getLicenseAttestations(subscriptionId);
        if (!cancelled) {
          setData(result);
        }
      } catch (error) {
        if (!cancelled) {
          pushError(error);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionId]);

  const norms: any[] = data?.norms || [];

  const toggle = async (norm: any) => {
    const currentSubscriptionId = subscriptionId;
    const busyId = norm.normId;
    setBusy(busyId);
    try {
      await api.setLicenseAttestation({
        subscriptionId: currentSubscriptionId,
        normId: norm.normId,
        eliId: norm.eliId,
        status: norm.attestation ? "revoked" : "attested",
      });
      if (subscriptionId === currentSubscriptionId) {
        await load();
      }
    } catch (error) {
      pushError(error);
    } finally {
      setBusy((current) => (current === busyId ? null : current));
    }
  };

  // Filters over the already-scoped set: by provider + live Regelwerk text.
  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return norms.filter(
      (n) =>
        (!filterProvider || n.provider === filterProvider) &&
        (!q || `${n.shortName} ${n.title}`.toLowerCase().includes(q)),
    );
  }, [norms, filterProvider, filterText]);

  // Group the filtered set by rights holder.
  const groups = useMemo(() => {
    const by: Record<string, { providerName: string; channel: string; items: any[] }> = {};
    for (const n of filtered) {
      const g = (by[n.provider] ??= { providerName: n.providerName, channel: n.channel, items: [] });
      g.items.push(n);
    }
    return Object.entries(by).sort((a, b) => (a[1].providerName || "").localeCompare(b[1].providerName || ""));
  }, [filtered]);

  const providerOptions = useMemo(() => {
    const seen: Record<string, string> = {};
    norms.forEach((n) => (seen[n.provider] = n.providerName));
    return [
      { key: ALL_PROVIDERS, text: t("licenseAttestation.allProviders", "alle Anbieter") },
      ...Object.entries(seen).map(([k, v]) => ({ key: k, text: v })),
    ];
  }, [norms, t]);

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex min-w-0 flex-col gap-2">
        <h3 className="text-lg font-semibold">{t("licenseAttestation.title", "Lizenzen (Stand der Technik)")}</h3>
        <p className="max-w-[760px] text-sm text-muted-foreground">
          {t("licenseAttestation.intro",
            "Diese Konformitätsklasse nutzt lizenzpflichtige Normen. Bestätige je Norm, dass dein " +
            "Unternehmen eine gültige Lizenz besitzt — oder erwirb sie über das Bestellformular des Anbieters.")}
        </p>
      </div>

      {/* subscription + filters */}
      <div className="flex min-w-0 flex-wrap items-end gap-4">
        <div className="flex min-w-[240px] flex-col gap-1.5">
          <Label htmlFor={subscriptionSelectId}>{t("licenseAttestation.subscription", "Subscription")}</Label>
          <Select
            value={subscriptionId || undefined}
            onValueChange={(value: string) => setSubscriptionId(value)}
            disabled={!bindingsLoaded || bindings.length === 0}
          >
            <SelectTrigger id={subscriptionSelectId} className="w-[320px] max-w-full">
              <SelectValue
                placeholder={
                  !bindingsLoaded
                    ? t("licenseAttestation.loadingSubscriptions", "Abonnements werden geladen …")
                    : bindings.length === 0
                      ? t("licenseAttestation.noSubscriptions", "Kein Abonnement in diesem Mandanten")
                      : t("global.pleaseSelect")
                }
              />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)]">
              {bindings.map((b) => (
                <SelectItem key={String(b._id)} value={String(b._id)} className="whitespace-normal">
                  {subscriptionLabel(b)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={providerSelectId}>{t("licenseAttestation.provider", "Anbieter")}</Label>
          <Select
            value={filterProvider || ALL_PROVIDERS}
            onValueChange={(value: string) => setFilterProvider(value === ALL_PROVIDERS ? "" : value)}
          >
            <SelectTrigger id={providerSelectId} className="w-[180px] max-w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)]">
              {providerOptions.map((option) => (
                <SelectItem key={option.key} value={option.key} className="whitespace-normal">
                  {option.text}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex min-w-[200px] flex-col gap-1.5">
          <Label htmlFor={searchId}>{t("licenseAttestation.search", "Regelwerk suchen")}</Label>
          <Input
            id={searchId}
            className="w-[220px] max-w-full"
            value={filterText}
            onChange={(event) => setFilterText(event.target.value)}
          />
        </div>
      </div>

      {/* sharing notice — transparency is real: attestations go to the respective providers */}
      <Alert variant="info">
        <Info aria-hidden className="h-4 w-4" />
        <AlertDescription>
          {t("licenseAttestation.sharingNotice",
            "Deine Bestätigungen werden zur Lizenzprüfung mit den jeweiligen Normungsanbietern geteilt " +
            "und für ein Audit protokolliert.")}
        </AlertDescription>
      </Alert>

      {subscriptionId && data === null ? (
        <div className="flex flex-col gap-3" role="status" aria-live="polite">
          <span className="sr-only">{t("global.loading")}</span>
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : null}

      {groups.length === 0 && data !== null && (
        <div className="flex flex-col gap-2 rounded-lg border border-dashed p-6 text-center">
          <p className="text-sm font-semibold text-foreground">
            {norms.length === 0
              ? t("licenseAttestation.empty", "Für diese Subscription und deinen Anlagenbestand sind keine lizenzpflichtigen Normen relevant.")
              : t("licenseAttestation.emptyFiltered", "Keine Normen entsprechen den aktuellen Filtern.")}
          </p>
          {norms.length > 0 ? (
            <p className="mx-auto max-w-prose text-sm text-muted-foreground">
              {t("licenseAttestation.emptyFilteredHint",
                "Setze den Anbieter auf „alle Anbieter“ zurück oder leere das Suchfeld, um wieder alle Normen zu sehen.")}
            </p>
          ) : null}
        </div>
      )}

      {groups.map(([key, g]) => (
        <div key={key} className="flex min-w-0 flex-col gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-4 border-b pb-2">
            <ProviderLogo provider={key} name={g.providerName} />
            <span className="min-w-0 break-words text-sm text-muted-foreground">
              {t("licenseAttestation.via", "Bezug über")}: {g.channel}
            </span>
          </div>
          {g.items.map((n) => {
            const checkboxId = `${fieldId}-${n.normId}`;
            const assuranceId = `${fieldId}-${n.normId}-assurance`;
            return (
              <div key={n.normId} className="flex min-w-0 flex-wrap items-start gap-4 border-b pb-3">
                <div className="flex items-center gap-2 pt-0.5">
                  <Checkbox
                    id={checkboxId}
                    checked={!!n.attestation}
                    disabled={!canWrite || busy === n.normId}
                    aria-describedby={data?.assuranceText ? assuranceId : undefined}
                    onCheckedChange={() => toggle(n)}
                  />
                  {busy === n.normId ? (
                    <Loader2 aria-hidden className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="min-w-0 break-words">
                    <Label htmlFor={checkboxId} className="font-semibold">{n.shortName}</Label>
                    <span className="ml-2 text-sm font-normal text-muted-foreground">{n.title}</span>
                  </div>
                  <div className="min-w-0 break-words text-sm text-muted-foreground">
                    {(n.regulatedObjects || []).map((c: string) => regulatedObjectName(t, c)).join(" · ")}
                  </div>
                  {/* the checkbox binds the scope-accurate assurance, not a bare "license exists" */}
                  <p id={assuranceId} className="mt-0.5 min-w-0 break-words text-sm">
                    {data?.assuranceText}
                  </p>
                  {n.attestation && (
                    <p className="mt-0.5 text-xs text-success">
                      {t("licenseAttestation.attestedBy", {
                        by: n.attestation.attestedBy,
                        at: new Date(n.attestation.attestedAt).toLocaleString(),
                        defaultValue: "Bestätigt von {{by}} am {{at}}",
                      })}
                    </p>
                  )}
                </div>
                <a
                  href={n.orderUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex shrink-0 items-center gap-1 text-sm underline underline-offset-2 hover:no-underline"
                >
                  {t("licenseAttestation.orderForm", "Bestellformular")}
                  <ExternalLink aria-hidden className="h-3 w-3" />
                </a>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
